/**
 * Rescisão (R3/R4) — verbas rescisórias como documento de cálculo por pessoa:
 * lista das rescisões, criação/edição da configuração (desligamento, motivo,
 * aviso prévio indenizado, férias vencidas), cálculo pelo motor, recibo
 * rescisório (relatório HTML) e fechamento — que faz o DESLIGAMENTO
 * CADASTRAL (contrato encerrado + cadastro inativo).
 *
 * Quando a pessoa tem espelho de desligamento vindo do Convenia
 * (pessoa_desligamentos), o formulário pré-preenche a data e mostra o tipo
 * registrado lá — o MOTIVO fica sempre editável (o espelho pode divergir do
 * enquadramento real; ex.: "pedido pelo colaborador" no Convenia × término
 * de experiência pelo empregador com aviso indenizado no recibo real).
 */
import { Fragment, useEffect, useState } from "react";
import { Button, Card, Divider, Group, NumberInput, Select, Text, Textarea, Title } from "@mantine/core";
import { IconArrowLeft, IconCalculator, IconLock, IconPencil, IconPlus, IconPrinter, IconTrash } from "@tabler/icons-react";
import { api } from "../api";
import { fmtCompetencia, fmtData, fmtMoeda } from "../format";
import { navegar, useQueryRota, origemCompetencia, rotuloVoltarCompetencia } from "../roteador";
import { TabelaDados, Th, TdNum } from "../componentes/listas";
import { BadgeEstadoProcessamento, VisualizadorRelatorio, type RelatorioAberto } from "../componentes/relatorio";
import { CompetenciaField, DataField } from "../componentes/entradas-mantine";
import { Confirmacao, ErroApiAlert, notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { ReabrirProcessamento } from "../componentes/reabrir-processamento";
import { TabelaEventosResultado, type EventoResultado } from "./FeriasPage";

/** Espelho do enum do motor (db/processamentos.ts — ROTULO_MOTIVO_RESCISAO). */
const MOTIVOS = [
  { value: "termino_experiencia_empregado", label: "Término de contrato de experiência (antecipado pelo empregado)" },
  { value: "termino_experiencia_empregador", label: "Término de contrato de experiência (antecipado pelo empregador)" },
  { value: "pedido_demissao", label: "Pedido de demissão" },
  { value: "dispensa_sem_justa_causa", label: "Dispensa sem justa causa" },
  { value: "justa_causa", label: "Dispensa por justa causa" },
  { value: "acordo_mutuo_484a", label: "Acordo mútuo (CLT art. 484-A)" },
];

const ROTULO_MOTIVO: Record<string, string> = Object.fromEntries(MOTIVOS.map((m) => [m.value, m.label]));

interface FuncionarioOpcao {
  id: string;
  nome: string;
  matriculaDominio: number | null;
  ativo: boolean;
  desligamento: { dataDesligamento: string; tipoTitulo: string | null } | null;
}

interface RescisaoLista {
  id: string;
  estado: string;
  funcionarioId: string;
  competenciaPagamento: number;
  dataInicio: string | null;
  motivoRescisao: string | null;
  diasAvisoPrevio: number | null;
  diasFeriasVencidas: number | null;
  observacao: string | null;
  funcionario: { id: string; nome: string; matriculaDominio: number | null } | null;
  resumoRun: { liquidoCentavos: number; executadoEm: string; motorVersion: string } | null;
}

interface DetalheRescisao extends RescisaoLista {
  runs: { id: string; motorVersion: string; executadoEm: string }[];
  ultimaRun: {
    id: string;
    motorVersion: string;
    executadoEm: string;
    resultado: {
      pessoas: {
        codigoDominio: number;
        nome: string;
        eventos: EventoResultado[];
        totais: { proventosCentavos: number; descontosCentavos: number; liquidoCentavos: number; fgtsCentavos: number };
      }[];
    };
  } | null;
}

/** Formulário de criação/edição da configuração da rescisão. */
function FormRescisao({
  funcionarios,
  edicao,
  onSalvo,
  onCancelar,
}: {
  funcionarios: FuncionarioOpcao[];
  /** processamento em edição (null = criação) */
  edicao: RescisaoLista | null;
  onSalvo: () => Promise<void>;
  onCancelar: () => void;
}) {
  const [funcionarioId, setFuncionarioId] = useState<string | null>(edicao?.funcionarioId ?? null);
  const [dataDesligamento, setDataDesligamento] = useState<string | null>(edicao?.dataInicio ?? null);
  const [competencia, setCompetencia] = useState<number | null>(edicao?.competenciaPagamento ?? null);
  const [motivo, setMotivo] = useState<string | null>(edicao?.motivoRescisao ?? null);
  const [diasAviso, setDiasAviso] = useState<number | null>(edicao?.diasAvisoPrevio ?? null);
  const [diasFeriasVencidas, setDiasFeriasVencidas] = useState<number | null>(edicao?.diasFeriasVencidas ?? null);
  const [obs, setObs] = useState(edicao?.observacao ?? "");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);

  const funcionarioSel = funcionarios.find((f) => f.id === funcionarioId) ?? null;

  function escolherFuncionario(id: string | null) {
    setFuncionarioId(id);
    const f = funcionarios.find((x) => x.id === id);
    // pré-preenchimento pelo espelho do Convenia (data) — motivo NUNCA é
    // deduzido automaticamente: a escolha é decisão do gestor
    if (f?.desligamento && !edicao) {
      setDataDesligamento(f.desligamento.dataDesligamento);
      const [a, m] = f.desligamento.dataDesligamento.split("-").map(Number);
      setCompetencia(a * 100 + m);
    }
  }

  function aoMudarData(d: string | null) {
    setDataDesligamento(d);
    if (d) {
      const [a, m] = d.split("-").map(Number);
      setCompetencia(a * 100 + m);
    }
  }

  async function salvar() {
    setOcupado(true);
    setErro(null);
    try {
      const corpo = {
        tipo: "rescisao",
        funcionarioId,
        competenciaPagamento: competencia,
        dataInicio: dataDesligamento,
        motivoRescisao: motivo,
        diasAvisoPrevio: diasAviso,
        diasFeriasVencidas,
        observacao: obs.trim() || null,
      };
      if (edicao) {
        await api.patch(`/processamentos/${edicao.id}`, corpo);
        notificarOk("Rescisão atualizada.");
      } else {
        await api.post("/processamentos", corpo);
        notificarOk("Rescisão criada — calcule para gerar as verbas.");
      }
      await onSalvo();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setOcupado(false);
    }
  }

  const opcoes = funcionarios.map((f) => ({
    value: f.id,
    label:
      `${f.nome}${f.matriculaDominio !== null ? ` (mat. ${f.matriculaDominio})` : " (sem matrícula)"}` +
      `${f.desligamento ? ` — deslig. ${fmtData(f.desligamento.dataDesligamento)}` : ""}`,
  }));

  return (
    <Card withBorder shadow="xs" mb="md" data-testid="form-rescisao">
      <Title order={4} mb="xs">{edicao ? "Editar rescisão" : "Nova rescisão"}</Title>
      <Group align="flex-end" wrap="wrap">
        <Select
          label="Pessoa (CLT)"
          placeholder="Escolha"
          data={opcoes}
          value={funcionarioId}
          onChange={escolherFuncionario}
          searchable
          disabled={edicao !== null}
          w={380}
        />
        <DataField label="Data do desligamento" valor={dataDesligamento} onChange={aoMudarData} />
        <CompetenciaField label="Competência de pagamento" valor={competencia} onChange={setCompetencia} />
        <Select
          label="Motivo (enquadramento das verbas)"
          placeholder="Escolha"
          data={MOTIVOS}
          value={motivo}
          onChange={setMotivo}
          w={420}
        />
        <NumberInput
          label="Aviso prévio indenizado (dias)"
          value={diasAviso ?? ""}
          onChange={(v) => setDiasAviso(typeof v === "number" ? v : null)}
          min={0}
          max={90}
          w={190}
        />
        <NumberInput
          label="Férias vencidas (dias)"
          value={diasFeriasVencidas ?? ""}
          onChange={(v) => setDiasFeriasVencidas(typeof v === "number" ? v : null)}
          min={0}
          max={60}
          w={170}
        />
        <Textarea label="Observação (opcional)" value={obs} onChange={(e) => setObs(e.currentTarget.value)} minRows={1} w={300} />
      </Group>
      {funcionarioSel?.desligamento && (
        <Text size="xs" c="dimmed" mt="xs">
          Espelho Convenia: desligamento {fmtData(funcionarioSel.desligamento.dataDesligamento)}
          {funcionarioSel.desligamento.tipoTitulo ? ` · "${funcionarioSel.desligamento.tipoTitulo}"` : ""} — confira o motivo
          acima: o registro do Convenia pode divergir do enquadramento real das verbas.
        </Text>
      )}
      <ErroApiAlert erro={erro} />
      <Group mt="sm" justify="flex-end">
        <Button variant="default" onClick={onCancelar}>Cancelar</Button>
        <Button
          leftSection={<IconPlus size={16} />}
          loading={ocupado}
          disabled={!funcionarioId || !competencia || !dataDesligamento || !motivo}
          onClick={() => void salvar()}
        >
          {edicao ? "Salvar alterações" : "Criar rescisão"}
        </Button>
      </Group>
    </Card>
  );
}

export function RescisaoPage() {
  // 03/09/2026 (pedido do dono) — deep-link da competência (passo 3): `?proc=<id>`
  // abre a rescisão certa direto e `?origem=/competencias/AAAAMM` mostra o botão
  // "Voltar para a competência MM/AAAA" (mesmo padrão das páginas de Férias)
  const queryRota = useQueryRota();
  const origem = origemCompetencia(queryRota);
  const [lista, setLista] = useState<RescisaoLista[]>([]);
  const [funcionarios, setFuncionarios] = useState<FuncionarioOpcao[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [formAberto, setFormAberto] = useState(false);
  const [edicao, setEdicao] = useState<RescisaoLista | null>(null);
  const [aberta, setAberta] = useState<string | null>(() => queryRota.get("proc"));
  const [detalhe, setDetalhe] = useState<DetalheRescisao | null>(null);
  const [relatorio, setRelatorio] = useState<RelatorioAberto | null>(null);
  const [confirmar, setConfirmar] = useState<{ titulo: string; consequencia: string; rotulo: string; acao: () => Promise<unknown> } | null>(null);

  async function carregar() {
    const [r, f] = await Promise.all([
      api.get<{ processamentos: RescisaoLista[] }>("/processamentos?tipo=rescisao"),
      api.get<{ funcionarios: FuncionarioOpcao[] }>("/funcionarios?tipoVinculo=clt"),
    ]);
    setLista(r.processamentos);
    setFuncionarios(f.funcionarios);
    if (aberta) {
      setDetalhe(await api.get<DetalheRescisao>(`/processamentos/${aberta}`));
    }
  }
  useEffect(() => {
    carregar().catch(setErro);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberta]);

  async function acao(fn: () => Promise<unknown>, mensagem: string) {
    setOcupado(true);
    setErro(null);
    try {
      await fn();
      notificarOk(mensagem);
      await carregar();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setOcupado(false);
      setConfirmar(null);
    }
  }

  function alternarDetalhe(id: string) {
    setDetalhe(null);
    setAberta(aberta === id ? null : id);
  }

  const pessoaDoDetalhe = detalhe?.ultimaRun?.resultado.pessoas[0] ?? null;

  return (
    <>
      {origem && (
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={16} />}
          mb="xs"
          data-testid="voltar-competencia"
          onClick={() => navegar(origem)}
        >
          {rotuloVoltarCompetencia(origem)}
        </Button>
      )}
      <Group justify="space-between" mb="xs" wrap="wrap">
        <div>
          <Title order={2}>Rescisão</Title>
          <Text size="sm" c="dimmed">
            Verbas rescisórias por pessoa (saldo de salário, aviso prévio, 13º e férias proporcionais/vencidas,
            descontos e FGTS). Fechar a rescisão faz o desligamento cadastral: encerra o contrato e inativa a pessoa.
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          variant={formAberto && !edicao ? "light" : "filled"}
          onClick={() => {
            setEdicao(null);
            setFormAberto(true);
          }}
        >
          Nova rescisão
        </Button>
      </Group>
      <ErroApiAlert erro={erro} />

      {(formAberto || edicao) && (
        <FormRescisao
          key={edicao?.id ?? "novo"}
          funcionarios={funcionarios}
          edicao={edicao}
          onSalvo={async () => {
            setFormAberto(false);
            setEdicao(null);
            await carregar();
          }}
          onCancelar={() => {
            setFormAberto(false);
            setEdicao(null);
          }}
        />
      )}

      <TabelaDados>
        <thead>
          <tr>
            <Th>Pessoa</Th>
            <Th>Desligamento</Th>
            <Th>Motivo</Th>
            <Th>Pagamento</Th>
            <Th>Estado</Th>
            <Th num>Líquido</Th>
            <Th>Ações</Th>
          </tr>
        </thead>
        <tbody>
          {lista.map((r) => (
            <Fragment key={r.id}>
              <tr>
                <td>
                  <Button variant="subtle" size="compact-sm" onClick={() => alternarDetalhe(r.id)}>
                    {r.funcionario?.nome ?? "—"}
                  </Button>
                </td>
                <td>{r.dataInicio ? fmtData(r.dataInicio) : "—"}</td>
                <td>
                  <Text size="sm">{r.motivoRescisao ? (ROTULO_MOTIVO[r.motivoRescisao] ?? r.motivoRescisao) : "—"}</Text>
                </td>
                <td>{fmtCompetencia(r.competenciaPagamento)}</td>
                <td>
                  <BadgeEstadoProcessamento estado={r.estado} />
                </td>
                <TdNum>{r.resumoRun ? <strong>{fmtMoeda(r.resumoRun.liquidoCentavos)}</strong> : "—"}</TdNum>
                <td>
                  <Group gap={4} wrap="nowrap">
                    {r.estado !== "fechado" && (
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconCalculator size={14} />}
                        loading={ocupado}
                        onClick={() =>
                          r.resumoRun
                            ? setConfirmar({
                                titulo: "Recalcular a rescisão?",
                                consequencia: "Uma NOVA execução é gravada (a anterior fica no histórico).",
                                rotulo: "Recalcular",
                                acao: () => api.post(`/processamentos/${r.id}/calcular`),
                              })
                            : void acao(() => api.post(`/processamentos/${r.id}/calcular`), "Rescisão calculada.")
                        }
                      >
                        {r.resumoRun ? "Recalcular" : "Calcular"}
                      </Button>
                    )}
                    {r.resumoRun && (
                      <Button
                        size="compact-xs"
                        variant="light"
                        leftSection={<IconPrinter size={14} />}
                        loading={ocupado}
                        onClick={() =>
                          void acao(async () => {
                            const art = (await api.post(`/processamentos/${r.id}/relatorio`)) as { id: string; nomeArquivo: string };
                            setRelatorio({ artefatoId: art.id, nomeArquivo: art.nomeArquivo, titulo: `Recibo rescisório — ${r.funcionario?.nome ?? ""}` });
                          }, "Recibo pronto.")
                        }
                      >
                        Recibo
                      </Button>
                    )}
                    {r.estado === "calculado" && (
                      <Button
                        size="compact-xs"
                        color="teal"
                        leftSection={<IconLock size={14} />}
                        onClick={() =>
                          setConfirmar({
                            titulo: `Fechar a rescisão de ${r.funcionario?.nome ?? ""}?`,
                            consequencia:
                              "Fechada é imutável E faz o desligamento cadastral: o contrato vigente é encerrado " +
                              "na competência do desligamento e o cadastro da pessoa fica INATIVO (ela sai das folhas seguintes).",
                            rotulo: "Fechar e desligar",
                            acao: () => api.post(`/processamentos/${r.id}/fechar`),
                          })
                        }
                      >
                        Fechar
                      </Button>
                    )}
                    {r.estado === "fechado" && (
                      <ReabrirProcessamento
                        processamentoId={r.id}
                        tipoRotulo="rescisão"
                        onReaberto={() => void carregar()}
                      />
                    )}
                    {r.estado !== "fechado" && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        leftSection={<IconPencil size={14} />}
                        onClick={() => {
                          setFormAberto(false);
                          setEdicao(r);
                        }}
                      >
                        Editar
                      </Button>
                    )}
                    {r.estado === "aberto" && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={() =>
                          setConfirmar({
                            titulo: "Excluir a rescisão?",
                            consequencia: "Só é possível excluir em \"aberto\" (sem cálculo gravado).",
                            rotulo: "Excluir",
                            acao: () => api.del(`/processamentos/${r.id}`),
                          })
                        }
                      >
                        Excluir
                      </Button>
                    )}
                  </Group>
                </td>
              </tr>
              {aberta === r.id && (
                <tr>
                  <td colSpan={7}>
                    {!detalhe || detalhe.id !== r.id ? (
                      <Text size="sm" c="dimmed">Carregando…</Text>
                    ) : (
                      <>
                        <Text size="xs" c="dimmed" mb="xs">
                          {detalhe.runs.length} execução(ões)
                          {detalhe.ultimaRun ? ` · última ${fmtData(detalhe.ultimaRun.executadoEm.slice(0, 10))} (${detalhe.ultimaRun.motorVersion})` : ""}
                          {detalhe.diasAvisoPrevio ? ` · aviso prévio indenizado ${detalhe.diasAvisoPrevio}d` : ""}
                          {detalhe.diasFeriasVencidas ? ` · férias vencidas ${detalhe.diasFeriasVencidas}d` : ""}
                          {detalhe.observacao ? ` · ${detalhe.observacao}` : ""}
                        </Text>
                        {pessoaDoDetalhe && (
                          <>
                            <Group gap="xl" mb="sm" wrap="wrap">
                              <div>
                                <Text size="xs" c="dimmed">Proventos</Text>
                                <Text fw={700}>{fmtMoeda(pessoaDoDetalhe.totais.proventosCentavos)}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">Descontos</Text>
                                <Text fw={700}>{fmtMoeda(pessoaDoDetalhe.totais.descontosCentavos)}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">Líquido rescisório</Text>
                                <Text fw={700} c="sillis.7" size="lg">{fmtMoeda(pessoaDoDetalhe.totais.liquidoCentavos)}</Text>
                              </div>
                              <div>
                                <Text size="xs" c="dimmed">FGTS (8%)</Text>
                                <Text fw={700}>{fmtMoeda(pessoaDoDetalhe.totais.fgtsCentavos)}</Text>
                              </div>
                            </Group>
                            <Group align="flex-start" grow wrap="wrap">
                              <div>
                                <Text size="xs" fw={600} mb={4}>Proventos</Text>
                                <TabelaEventosResultado eventos={pessoaDoDetalhe.eventos} tipo="P" />
                              </div>
                              <div>
                                <Text size="xs" fw={600} mb={4}>Descontos</Text>
                                <TabelaEventosResultado eventos={pessoaDoDetalhe.eventos} tipo="D" />
                              </div>
                            </Group>
                          </>
                        )}
                        {!detalhe.ultimaRun && (
                          <Text size="sm" c="dimmed">Ainda não calculada — use “Calcular”.</Text>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
          {lista.length === 0 && (
            <tr>
              <td colSpan={7}>
                <Text size="sm" c="dimmed">Nenhuma rescisão registrada — use “Nova rescisão”.</Text>
              </td>
            </tr>
          )}
        </tbody>
      </TabelaDados>

      <Divider my="md" />
      <Text size="xs" c="dimmed">
        Uma rescisão por vínculo. O motivo define as verbas devidas (aviso prévio indenizado, multa FGTS, saque FGTS,
        férias/13º proporcionais). Divergências legítimas × Contmega documentadas no R3 (códigos 292/345, INSS 8130).
      </Text>

      <VisualizadorRelatorio relatorio={relatorio} onFechar={() => setRelatorio(null)} />
      <Confirmacao
        aberta={confirmar !== null}
        titulo={confirmar?.titulo ?? ""}
        consequencia={confirmar?.consequencia ?? ""}
        rotuloConfirmar={confirmar?.rotulo}
        onConfirmar={() => void acao(() => confirmar!.acao(), "Pronto.")}
        onCancelar={() => setConfirmar(null)}
      />
    </>
  );
}
